import type { ReactNode } from 'react';

import type { ChassisTrace, ChassisUnitLabelProps } from '../Chassis/Chassis';
import type { ChassisRearPanelPort } from '../Layouts/ChassisPorts';
import type {
  ChassisFan,
  ChassisLayout,
  ChassisPort,
  ChassisPowerSupply,
  ChassisTemperature,
} from '../Layouts/types';
import { styled } from '../../stitches.config';
import { Chassis, ChassisUnit } from '../Chassis/Chassis';
import { ChassisPorts, ChassisRearPanel } from '../Layouts/ChassisPorts';
import {
  SwitchLayoutsCuttlefish,
  SwitchLayoutsMantaray,
  SwitchLayoutsMS10,
  SwitchLayoutsMS11,
  SwitchLayoutsMS12,
  SwitchLayoutsS1,
  SwitchLayoutsS2,
  SwitchLayoutsS3,
  SwitchLayoutsS4Primary,
  SwitchLayoutsS4Secondary,
} from '../Layouts/switch/SwitchLayouts';
import { LEDs } from '../LEDs/LEDs';
import { HardwareDeviceModel, isGen1Hardware } from '../utils';

export type SwitchModel =
  | HardwareDeviceModel.Ms10
  | HardwareDeviceModel.Ms11
  | HardwareDeviceModel.Ms12
  | HardwareDeviceModel.S1
  | HardwareDeviceModel.S2
  | HardwareDeviceModel.S3
  | HardwareDeviceModel.S4
  | HardwareDeviceModel.Cuttlefish
  | HardwareDeviceModel.Mantaray;

// Type guard to check if a DeviceModel is a valid SwitchModel
export function isSwitchModel(deviceModel: string): deviceModel is SwitchModel {
  return [
    HardwareDeviceModel.Ms10,
    HardwareDeviceModel.Ms11,
    HardwareDeviceModel.Ms12,
    HardwareDeviceModel.S1,
    HardwareDeviceModel.S2,
    HardwareDeviceModel.S3,
    HardwareDeviceModel.S4,
    HardwareDeviceModel.Cuttlefish,
    HardwareDeviceModel.Mantaray,
  ].includes(deviceModel as SwitchModel);
}

/**
 * Models that are two switches in one enclosure, and so are drawn as two units on one chassis.
 * Exported so callers can decide how many `units` to build — the counterpart of
 * `isDualUnitFirewall`.
 */
export function isDualSwitch(deviceModel: string): deviceModel is SwitchModel {
  return [HardwareDeviceModel.S4].includes(deviceModel as SwitchModel);
}

// Safe conversion function from DeviceModel to SwitchModel
export function toSwitchModel(deviceModel: string): SwitchModel | null {
  return isSwitchModel(deviceModel) ? deviceModel : null;
}

export type SwitchUnit = {
  /**
   * Distinguishes this unit from the others on the card. The device UUID does; anything stable per
   * unit works.
   */
  id: string;
  /** The vertical pill naming the unit. */
  label: ChassisUnitLabelProps;
  /** The unit's own ports, numbered as they are on its faceplate. */
  ports: ChassisPort[];
  /**
   * Number-chip text for this unit's out-of-band port, which is drawn in the panel shared with the
   * other units rather than in this unit's own columns. Defaults to `OOB`.
   */
  outOfBandLabel?: string;
  /** Fan bays, drawn in the rear panel's "Climate" bank. */
  fans?: ChassisFan[];
  /** Swappable power supply bays, drawn in the unit's rear panel. */
  powerSupplies?: ChassisPowerSupply[];
  /** Thermal sensor readings, drawn after any fans in the rear panel's "Climate" bank. */
  temperatures?: ChassisTemperature[];
};

export type SwitchProps = {
  model: SwitchModel;
  metadata?: ReactNode;
  /**
   * One entry per physical device on the card, left to right. Most models are a single unit; the
   * ones that ship as a pair (see `isDualSwitch`) expect two.
   */
  units: SwitchUnit[];
  /**
   * Lines joining ports that belong to one thing, drawn between their connection chips. A SPAN
   * session is one per source, each ending at the destination.
   */
  traces?: ChassisTrace[];
};

/**
 * Where a model's ports sit on its faceplate. A dual switch numbers both halves from 1 but names
 * them apart — `A-n` on the left, `B-n` on the right — and captions their banks with different
 * numbers, so each half has its own layout.
 */
export function getSwitchLayout(model: SwitchModel, isPrimary = true): ChassisLayout {
  switch (model) {
    case HardwareDeviceModel.Ms10:
      return SwitchLayoutsMS10;
    case HardwareDeviceModel.Ms11:
      return SwitchLayoutsMS11;
    case HardwareDeviceModel.Ms12:
      return SwitchLayoutsMS12;
    case HardwareDeviceModel.S1:
      return SwitchLayoutsS1;
    case HardwareDeviceModel.S2:
      return SwitchLayoutsS2;
    case HardwareDeviceModel.S3:
      return SwitchLayoutsS3;
    case HardwareDeviceModel.S4:
      return isPrimary ? SwitchLayoutsS4Primary : SwitchLayoutsS4Secondary;
    case HardwareDeviceModel.Cuttlefish:
      return SwitchLayoutsCuttlefish;
    case HardwareDeviceModel.Mantaray:
      return SwitchLayoutsMantaray;
    default:
      return SwitchLayoutsMS10;
  }
}

export function getSwitchPortOrientation(
  model: SwitchModel,
  portNumber: number,
): 'up' | 'down' | undefined {
  const layout = getSwitchLayout(model);

  for (const section of layout.sections) {
    for (const column of section.columns) {
      for (const row of column.rows) {
        if (row.ports.includes(portNumber)) {
          return row.portOrientation;
        }
      }
    }
  }

  return undefined;
}

const SwitchMetadata = styled('div', {
  display: 'flex',
  justifyContent: 'space-between',
  width: '100%',
});
SwitchMetadata.displayName = 'SwitchMetadata';

/**
 * Out-of-band ports of a dual switch, gathered into one panel at the trailing edge of the card
 * instead of one per unit. Each entry carries its own unit's ports, so both halves numbering their
 * management port 0 doesn't make them ambiguous.
 */
function getOutOfBandPanelPorts(units: SwitchUnit[]): ChassisRearPanelPort[] {
  return units.flatMap((unit) => {
    const port = unit.ports.find((candidate) => candidate.isOutOfBandPort);
    if (!port) return [];

    return [
      {
        id: unit.id,
        ports: unit.ports,
        number: port.number,
        type: 'ethernet' as const,
        label: unit.outOfBandLabel ?? 'OOB',
      },
    ];
  });
}

export function Switch({ metadata, model, traces, units }: SwitchProps) {
  // Only a pair gathers its management ports; a single switch's layout places its own.
  const outOfBandPorts = isDualSwitch(model) ? getOutOfBandPanelPorts(units) : [];
  const gen1Hardware = isGen1Hardware(model);

  return (
    <Chassis metadata={metadata && <SwitchMetadata>{metadata}</SwitchMetadata>} traces={traces}>
      {units.map((unit, unitIndex) => (
        <ChassisUnit key={unit.id} label={unit.label} leds={gen1Hardware && <LEDs />}>
          <ChassisPorts
            fans={unit.fans}
            layout={getSwitchLayout(model, unitIndex === 0)}
            ports={unit.ports}
            powerSupplies={unit.powerSupplies}
            temperatures={unit.temperatures}
          />
          {/* The shared panel hangs off the last unit, so it lands at the card's trailing edge. */}
          {unitIndex === units.length - 1 && outOfBandPorts.length > 0 && (
            <ChassisRearPanel ports={outOfBandPorts} />
          )}
        </ChassisUnit>
      ))}
    </Chassis>
  );
}
